/*
 * Where the Agents menu and the banners live: beside the sidebar's Agents
 * button, bottom edge level with it, so banners grow out of (and are pulled
 * back into) the button and the menu opens in the same place.
 */
import {
  useEffect,
  useLayoutEffect,
  useState,
  type CSSProperties,
  type FC
} from "react";
import { useLocation } from "react-router-dom";

import { useAgentsTrayStore } from "../context/agentsTrayStore";
import { AgentsMenu } from "./AgentsMenu";
import { SignalAlerts } from "./SignalAlerts";

const GAP = 6;
const FALLBACK: CSSProperties = { left: 54, bottom: 42 };

const useAnchorPosition = (anchor: HTMLElement | null): CSSProperties => {
  const [pos, setPos] = useState<CSSProperties>(FALLBACK);
  useLayoutEffect(() => {
    if (!anchor) {
      setPos(FALLBACK);
      return undefined;
    }
    const measure = () => {
      const r = anchor.getBoundingClientRect();
      if (!r.width && !r.height) {
        setPos(FALLBACK);
        return;
      }
      // Past the sidebar's edge, not just the button's.
      const rail = anchor.closest<HTMLElement>(
        "[data-slot='sidebar-container']"
      );
      const right = rail ? rail.getBoundingClientRect().right : r.right;
      setPos({
        left: Math.round(right + GAP),
        bottom: Math.max(GAP, Math.round(window.innerHeight - r.bottom))
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(anchor);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [anchor]);
  return pos;
};

export const AgentAttentionLayer: FC = () => {
  const open = useAgentsTrayStore((s) => s.open);
  const setOpen = useAgentsTrayStore((s) => s.setOpen);
  const anchor = useAgentsTrayStore((s) => s.anchor);
  const pos = useAnchorPosition(anchor);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname, setOpen]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (anchor?.contains(t)) return;
      if ((t as Element).closest?.("[data-agents-layer]")) return;
      setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [open, anchor, setOpen]);

  return (
    <div data-agents-layer="" className="fixed z-50" style={pos}>
      {open ? <AgentsMenu /> : null}
      <SignalAlerts />
    </div>
  );
};
